import {
  useMutation,
  UseQueryResult,
  useQueryClient
} from "@tanstack/react-query";
import { Applicant, Application } from "types/application";
import { PendingReportView } from "views/applications-id-report/components/ReportsFallback/PendingReportView";
import { ProgressReportView } from "views/applications-id-report/components/ReportsFallback/ProgressReportView";
import {
  ApplicantSettings,
  ApplicantStatus,
  ApplicantType,
  ExecutionModes,
  RetryIdentityVerificationStatus,
  Verification,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { VerificationSchema } from "types/landlord/verificationTemplate";
import { TabStepsType } from "views/applications-id-report";
import { instance } from "api/apiBase";
import { logger } from "logger";
import { getErrorMessage } from "utils/error";
import { toast } from "react-toastify";

import { lazy, Suspense, useCallback, useMemo } from "react";

const AdditionalDocumentsSlice = lazy(
  () => import("../slices/AdditionalDocumentsSlice")
);
const ApplicantChatSlice = lazy(() => import("../slices/ApplicantChatSlice"));
const BasicInformation = lazy(() => import("../slices/BasicInformation"));
const CreditReportSlice = lazy(
  () => import("views/applications-id-report/slices/CreditReportSlice")
);
const CriminalReportSlice = lazy(() => import("../slices/CriminalReportSlice"));
const DisclosureSlice = lazy(() => import("../slices/DisclosureSlice"));
const EvictionReportSlice = lazy(() => import("../slices/EvictionReportSlice"));
const HousingHistoryReportSlice = lazy(
  () => import("../slices/HousingHistoryReportSlice")
);
const IdentityVerificationReportSlice = lazy(
  () => import("../slices/IdentityVerificationReportSlice")
);
const IncomeReportSlice = lazy(() => import("../slices/IncomeReportSlice"));

import { ManualReportView } from "views/applications-id-report/components/ReportsFallback/ManualReportView";
import { ProcessingReportView } from "views/applications-id-report/components/ReportsFallback/ProcessingReportView";
import { SkippedReportView } from "views/applications-id-report/components/ReportsFallback/SkippedReportView";
import { CreditReportBlockedView } from "views/applications-id-report/components/ReportsFallback/CreditReportBlockedView";
import { UnavailableReportView } from "./ReportsFallback/UnavailableReportView";
import { CanadianCriminalReportSlice } from "views/applications-id-report/slices/CanadianCriminalReportSlice";
import { Button, ButtonSize, ButtonVariant } from "shared/components/Button";
import { CombinedIncomeReportSlice } from "../slices/CombinedIncomeReportSlice";
import { PetScreeningReportView } from "./ReportsFallback/PetScreeningReportView";
import { handleCopy } from "utils/handleCopy";
import { useAuthContext } from "providers/Auth";
import { NoReportAccessView } from "./ReportsFallback/NoReportAccessView";
import { ReportHeader } from "./ReportHeader";
import { verificationNameToTitle } from "views/applications-id/components/ApplicantsTable/utils";

interface ReportsCardProps {
  applicant?: Applicant;
  currentTab: TabStepsType;
  applicationMeta: UseQueryResult<Application | undefined, unknown>;
  currentVerification?: Verification;
  screeningCriteria?: ApplicantSettings;
  verificationSchemas?: {
    [Key in VerificationNames]: VerificationSchema;
  };
}

const cancelRetryErrorMessageMap = {
  already_completed:
    "Unable to cancel retry attempt because the applicant has already completed their identity verification"
};

export const ReportsCard = ({
  applicant,
  currentTab,
  applicationMeta,
  currentVerification,
  screeningCriteria,
  verificationSchemas
}: ReportsCardProps) => {
  const { permissions } = useAuthContext();
  const queryClient = useQueryClient();

  const isPermitted = useMemo(() => {
    switch (currentTab) {
      case VerificationNames.RentalApplication:
        return permissions.canAccessReports.rentalApplication;
      case VerificationNames.IdentityVerification:
        return permissions.canAccessReports.identityVerification;
      case VerificationNames.IncomeVerification:
      case VerificationNames.CombinedIncome:
      case VerificationNames.VerifiedIncomeVerification:
        return permissions.canAccessReports.incomeReport;
      case VerificationNames.HousingHistory:
        return permissions.canAccessReports.housingHistoryReport;
      case VerificationNames.CreditReport:
        return permissions.canAccessReports.creditReport;
      case VerificationNames.CriminalReport:
        return permissions.canAccessReports.usCriminalReport;
      case VerificationNames.EvictionReport:
        return permissions.canAccessReports.housingCourtReport;
      case VerificationNames.CanadianCriminalReport:
        return permissions.canAccessReports.caCriminalReport;
      case VerificationNames.PetScreeningReport:
        return permissions.canAccessReports.animalScreening;
      case VerificationNames.SubmissionDisclosure:
        return permissions.canAccessReports.disclosure;
      case "additional_documents":
        return permissions.canAccessReports.additionalDocumentsReport;
      case "chat":
        return true;
      default:
        return false;
    }
  }, [currentVerification, permissions]);

  const refetchApplication = useCallback(() => {
    queryClient
      .refetchQueries({
        queryKey: ["APPLICATION_ID_REPORT", String(applicationMeta.data?.id)]
      })
      .catch((error) => {
        logger.error("Failed to invalidate queries", error);
      });
  }, [applicationMeta.data, queryClient]);

  const { mutate: createRetryLink, isPending: createRetryLinkPending } =
    useMutation({
      mutationKey: [
        "APPLICATION",
        applicationMeta.data?.id,
        applicant?.id,
        "RETRY_VERIFICATION_LINK"
      ],
      mutationFn: () =>
        instance.post<string>(
          `/portal/v1/applications/${applicationMeta.data?.id}/applicants/${applicant?.id}/create_retry_identity_verification_link`
        ),
      onSuccess: ({ data }) => {
        refetchApplication();
        handleCopy(data, "The link has been copied to your clipboard");
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toast.error(
          `Request identity verification link failed: ${errorMessage}`
        );
      }
    });

  const { mutate: cancelRetryIdentityVerification, isPending } = useMutation({
    mutationFn: () =>
      instance.post(
        `/portal/v1/applications/${applicationMeta.data?.id}/applicants/${applicant?.id}/cancel_request_retry_identity_verification`
      ),
    onSuccess: () => {
      refetchApplication();
      toast.success("Identity verification retry successfully canceled");
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error);

      if (
        ["not_request_retry_yet", "already_completed"].includes(errorMessage)
      ) {
        refetchApplication();

        if (errorMessage === "not_request_retry_yet") {
          toast.success("Identity verification retry successfully canceled");
          return;
        }
      }

      toast.error(
        cancelRetryErrorMessageMap[errorMessage] ||
          `Cancel identity verification retry failed: ${errorMessage}`
      );
    }
  });

  if (!applicant || !applicationMeta.data) return null;

  if (!isPermitted) return <NoReportAccessView />;

  if (
    currentVerification?.name === VerificationNames.IdentityVerification &&
    [VerificationStatus.Pending, VerificationStatus.Started].includes(
      currentVerification.status
    ) &&
    [
      RetryIdentityVerificationStatus.Pending,
      RetryIdentityVerificationStatus.InProgress
    ].includes(
      applicant.retry_identity_verification_status ||
        RetryIdentityVerificationStatus.NotAvailable
    )
  )
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <div className="flex flex-col items-center">
          <ProcessingReportView
            className="!max-w-lg"
            currentVerification={currentVerification}
            applicant={applicant}
          />
          <div className="flex gap-2">
            <Button
              size={ButtonSize.BASE}
              variant={ButtonVariant.BASE}
              onClick={() => createRetryLink()}
              loading={createRetryLinkPending}
              disabled={createRetryLinkPending}
            >
              Copy retry link
            </Button>

            <Button
              size={ButtonSize.BASE}
              variant={ButtonVariant.PRIMARY}
              onClick={() => cancelRetryIdentityVerification()}
              loading={isPending}
              disabled={isPending}
            >
              Cancel retry
            </Button>
          </div>
        </div>
      </div>
    );

  const isIdentityVerificationOverrideRequired =
    applicationMeta.data.requires_overrides_identity_verification?.applicants.some(
      ({ id }) => id === applicant.id
    );

  if (
    currentVerification?.status === VerificationStatus.Pending &&
    currentVerification.settings?.execution_mode === ExecutionModes.Manual
  )
    return (
      <>
        <ReportHeader
          applicationStatus={applicationMeta.data.status}
          applicantId={applicant.id}
          applicationId={applicationMeta.data.id}
          title={verificationNameToTitle(currentVerification.name)}
          name={currentVerification.name}
          hidePrint
        />
        <ManualReportView
          refetch={applicationMeta.refetch}
          applicantStatus={
            applicationMeta.data.applicants.find(
              (appl) => appl.id === applicant.id
            )?.application_status
          }
          applicationStatus={applicationMeta.data.status}
          applicantId={applicant.id}
          verificationId={currentVerification.id}
          applicationId={applicationMeta.data.id}
          verificationName={currentVerification.name}
          creditReportStatus={
            applicant.verifications.find(
              (v) => v.name === VerificationNames.CreditReport
            )?.status
          }
        />
      </>
    );

  if (
    (currentVerification?.status === VerificationStatus.Pending ||
      currentVerification?.status === VerificationStatus.Skipped) &&
    currentVerification.settings?.execution_mode ===
      ExecutionModes.Conditionally
  )
    return (
      <SkippedReportView
        refetch={applicationMeta.refetch}
        applicantId={applicant.id}
        verificationId={currentVerification.id}
        applicationId={applicationMeta.data.id}
        verificationStatus={currentVerification.status}
      />
    );

  // Check if eviction report is blocked by credit report error (for automatic mode)
  const creditReport = applicant.verifications.find(
    (v) => v.name === VerificationNames.CreditReport
  );
  const isCreditReportFailed =
    creditReport &&
    [
      VerificationStatus.Failed,
      VerificationStatus.FinishedWithWarnings
    ].includes(creditReport.status);

  if (
    currentVerification?.status === VerificationStatus.Pending &&
    currentVerification.name === VerificationNames.EvictionReport &&
    applicant.application_status === ApplicantStatus.Submitted &&
    currentVerification.settings?.execution_mode !== ExecutionModes.Manual &&
    isCreditReportFailed
  )
    return (
      <>
        <ReportHeader
          applicationStatus={applicationMeta.data.status}
          applicantId={applicant.id}
          applicationId={applicationMeta.data.id}
          title={verificationNameToTitle(currentVerification.name)}
          name={currentVerification.name}
          hidePrint
        />
        <CreditReportBlockedView
          refetch={applicationMeta.refetch}
          applicantId={applicant.id}
          verificationId={currentVerification.id}
          applicationId={applicationMeta.data.id}
        />
      </>
    );

  if (
    currentVerification?.status === VerificationStatus.Pending &&
    currentVerification.name !== VerificationNames.PetScreeningReport &&
    (currentVerification.is_rerun ||
      applicant.application_status === ApplicantStatus.Submitted)
  )
    return (
      <ProgressReportView
        message={
          currentVerification.is_rerun
            ? "Report re-run in progress. Please wait"
            : undefined
        }
      />
    );

  if (
    isIdentityVerificationOverrideRequired &&
    currentVerification?.name &&
    [
      VerificationNames.CreditReport,
      VerificationNames.CriminalReport,
      VerificationNames.EvictionReport
    ].includes(currentVerification.name)
  )
    return (
      <PendingReportView message="Please review issues with applicant's identity verification report. This report will not run until identity is verified" />
    );

  const isVerifiedIncomeProcessing =
    currentVerification?.name === VerificationNames.IncomeVerification &&
    applicant.verifications.find(
      (ver) => ver.name === VerificationNames.VerifiedIncomeVerification
    )?.status === VerificationStatus.Processing;

  if (
    (currentVerification?.status === VerificationStatus.Processing &&
      currentVerification.name !== VerificationNames.PetScreeningReport) ||
    isVerifiedIncomeProcessing
  )
    return <ProcessingReportView currentVerification={currentVerification} />;

  if (currentVerification?.name === VerificationNames.PetScreeningReport)
    return (
      <PetScreeningReportView
        applicationStatus={applicationMeta.data.status}
        verification={currentVerification}
        applicant={applicant}
      />
    );
  if (currentVerification?.status === VerificationStatus.Pending)
    return <PendingReportView />;
  if (currentVerification?.status === VerificationStatus.Canceled)
    return <PendingReportView message="Report was canceled" />;
  if (currentVerification?.status === VerificationStatus.Started)
    return <ProgressReportView />;
  if (currentVerification?.status === VerificationStatus.NoDataAvailable)
    return <UnavailableReportView />;

  // We treat verified and failed as finished
  // if (currentVerification.status === "verified") return <p>Status is verified</p>;

  const verificationId = Number(currentVerification?.id);

  switch (currentTab) {
    case VerificationNames.RentalApplication:
      return (
        <Suspense fallback={null}>
          <BasicInformation
            verificationId={verificationId}
            applicant={applicant}
            applicationMeta={applicationMeta}
            email={applicant.email}
            phone={applicant.phone}
          />
        </Suspense>
      );
    case VerificationNames.CanadianCriminalReport:
      return (
        <Suspense fallback={null}>
          <CanadianCriminalReportSlice
            applicationId={String(applicationMeta.data.id)}
            applicant={applicant}
            isNotResident={
              currentVerification?.status === VerificationStatus.Skipped
            }
          />
        </Suspense>
      );
    case VerificationNames.IdentityVerification:
      return (
        <Suspense fallback={null}>
          <IdentityVerificationReportSlice
            applicationStatus={applicationMeta.data.status}
            requiresOverridesIdentityVerification={Boolean(
              applicationMeta.data.requires_overrides_identity_verification?.applicants.some(
                ({ id }) => id === applicant.id
              )
            )}
            retryIdentityVerificationStatus={
              applicant.retry_identity_verification_status
            }
            applicationId={String(applicationMeta.data.id)}
            verificationId={verificationId}
            applicant={applicant}
            templateTemplateSettings={
              applicationMeta.data.verification_template?.settings!
            }
          />
        </Suspense>
      );
    case VerificationNames.IncomeVerification:
    case VerificationNames.VerifiedIncomeVerification:
      return (
        <Suspense fallback={null}>
          <IncomeReportSlice
            applicationStatus={applicationMeta.data.status}
            applicant={applicant}
            screeningCriteria={screeningCriteria}
            verificationTemplate={applicationMeta.data.verification_template}
            templateRecommendationSettings={
              applicationMeta.data.verification_template?.recommendation_method
            }
          />
        </Suspense>
      );
    case VerificationNames.HousingHistory:
      return (
        <Suspense fallback={null}>
          <HousingHistoryReportSlice
            applicationId={applicationMeta.data.id}
            applicationStatus={applicationMeta.data.status}
            verificationId={verificationId}
            applicant={applicant}
            verificationSchemas={verificationSchemas}
          />
        </Suspense>
      );
    case VerificationNames.CreditReport:
      return (
        <Suspense fallback={null}>
          <CreditReportSlice
            applicationStatus={applicationMeta.data.status}
            applicant={applicant}
            verificationId={verificationId}
            pastDueAccountType={
              applicationMeta.data.verification_template?.screening_criteria
                ? applicationMeta.data.verification_template.screening_criteria[
                    `${applicant.role}_settings`
                  ]?.past_due_account?.past_due_account_rate
                : ""
            }
            screeningCriteria={screeningCriteria}
          />
        </Suspense>
      );
    case VerificationNames.EvictionReport:
      return (
        <Suspense fallback={null}>
          <EvictionReportSlice
            applicationStatus={applicationMeta.data.status}
            verificationId={verificationId}
            applicant={applicant}
            screeningCriteria={screeningCriteria}
          />
        </Suspense>
      );
    case VerificationNames.CriminalReport:
      return (
        <Suspense fallback={null}>
          <CriminalReportSlice
            applicationId={applicationMeta.data.id}
            applicationStatus={applicationMeta.data.status}
            verificationId={verificationId}
            applicant={applicant}
            screeningCriteria={screeningCriteria}
            allowedManualFips={[
              ...(applicationMeta.data.verification_template?.settings[
                applicant.role || ApplicantType.Applicant
              ]?.criminal_report?.manual_walkin_county_fips || []),
              ...(applicationMeta.data.verification_template?.settings[
                applicant.role || ApplicantType.Applicant
              ]?.criminal_report?.manual_walkin_statewide_fips || [])
            ]}
            manualSearchAllowed={
              applicationMeta.data.verification_template?.settings[
                applicant.role || ApplicantType.Applicant
              ]?.criminal_report?.allow_manual_county_search ||
              applicationMeta.data.verification_template?.settings[
                applicant.role || ApplicantType.Applicant
              ]?.criminal_report?.allow_manual_statewide_search ||
              false
            }
          />
        </Suspense>
      );
    case "additional_documents":
      return (
        <Suspense fallback={null}>
          <AdditionalDocumentsSlice applicant={applicant} />
        </Suspense>
      );
    case VerificationNames.SubmissionDisclosure: {
      const jsonSchema = currentVerification?.steps?.[0].json_schema as
        | { partner_name_in_submission_disclosure?: string }
        | undefined;

      return (
        <Suspense fallback={null}>
          <DisclosureSlice
            applicationStatus={applicationMeta.data.status}
            applicant={applicant}
            verificationId={verificationId}
            partnerNameInSubmissionDisclosure={
              jsonSchema?.partner_name_in_submission_disclosure ||
              applicationMeta.data.partner.name
            }
          />
        </Suspense>
      );
    }
    case "chat":
      return (
        <Suspense fallback={null}>
          <ApplicantChatSlice applicant={applicant} key={applicant.id} />
        </Suspense>
      );
    case VerificationNames.CombinedIncome:
      return (
        <Suspense fallback={null}>
          <CombinedIncomeReportSlice
            applicationStatus={applicationMeta.data.status}
            applicant={applicant}
            hasCreditReport={applicant.verifications.some(
              (v) => v.name === VerificationNames.CreditReport
            )}
            applicantId={applicant.id}
            verificationId={verificationId}
            applicationId={applicant.application_id}
          />
        </Suspense>
      );
    default:
      return null;
  }
};
